import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, Camera, MapPin, Check } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import Cropper from 'react-easy-crop';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { Area } from 'react-easy-crop';

interface Location {
  display_name: string;
  lat: number;
  lon: number;
  type: string;
}

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose }) => {
  const { currentUser, updateProfile, isLoading: isProfileUpdating } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const locationSearchRef = useRef<HTMLDivElement>(null);
  
  const [username, setUsername] = useState(currentUser?.username || '');
  const [bio, setBio] = useState(currentUser?.bio || '');
  const [photoURL, setPhotoURL] = useState(currentUser?.photoURL || '');
  const [locationQuery, setLocationQuery] = useState('');
  const [debouncedLocationQuery] = useDebounce(locationQuery, 500);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [showCropper, setShowCropper] = useState(false);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationSearchRef.current && !locationSearchRef.current.contains(event.target as Node)) {
        setLocations([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    const searchLocations = async () => {
      if (!debouncedLocationQuery.trim()) {
        setLocations([]);
        return;
      }

      setIsSearchingLocation(true);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/geocode`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ query: debouncedLocationQuery })
          }
        );

        if (!response.ok) throw new Error('Failed to fetch locations');
        
        const data = await response.json();
        setLocations(data);
      } catch (err) {
        console.error('Error searching locations:', err);
        setLocations([]);
      } finally {
        setIsSearchingLocation(false);
      }
    };

    searchLocations();
  }, [debouncedLocationQuery]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');

    // Read the file and show cropper
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setShowCropper(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const handleCropConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels || !currentUser) return;

    setIsUploading(true);
    setError('');

    try {
      // Get the cropped image as a blob
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      // Create a unique file name
      const fileName = `${currentUser.id}/${crypto.randomUUID()}.jpg`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, croppedImageBlob, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/jpeg'
        });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      setPhotoURL(publicUrl);
      setShowCropper(false);
      setImageSrc(null);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditCurrentPhoto = () => {
    if (!photoURL || photoURL === 'https://via.placeholder.com/200') return;

    setImageSrc(photoURL);
    setShowCropper(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const formatLocation = (displayName: string) => {
    const parts = displayName.split(', ');
    if (parts.length >= 2) {
      const city = parts[0];
      const country = parts[parts.length - 1];
      return `${city}, ${country}`;
    }
    return displayName;
  };

  const handleLocationSelect = (location: Location) => {
    const formattedLocation = formatLocation(location.display_name);
    setSelectedLocation(formattedLocation);
    setLocationQuery('');
    setLocations([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    try {
      await updateProfile({
        username: username.trim(),
        bio: bio.trim(),
        photoURL,
        location: selectedLocation
      });
      onClose();
    } catch (err) {
      setError('Failed to update profile');
    }
  };

  if (!isOpen) return null;

  if (showCropper && imageSrc) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />

        <div className="relative bg-gray-900/95 backdrop-blur-xl rounded-2xl w-full max-w-2xl">
          <div className="p-6 border-b border-gray-800">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Adjust Profile Photo</h2>
              <button
                onClick={handleCropCancel}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="relative h-96 bg-black">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Zoom
              </label>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCropCancel}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                disabled={isUploading}
                className="px-4 py-2 bg-gray-800/50 backdrop-blur-md text-white rounded-lg hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Save Photo</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-gray-900/90 backdrop-blur-xl rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900/90 backdrop-blur-xl p-6 border-b border-gray-800">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Edit Profile</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Profile Photo
              </label>
              <div className="flex flex-col items-center justify-center space-y-3">
                <div className="relative group">
                  <img
                    src={photoURL || 'https://via.placeholder.com/200'}
                    alt={username}
                    className="w-32 h-32 rounded-full object-cover border-2 border-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {isUploading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                    ) : (
                      <Camera className="h-8 w-8 text-white" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
                {photoURL && photoURL !== 'https://via.placeholder.com/200' && (
                  <button
                    type="button"
                    onClick={handleEditCurrentPhoto}
                    disabled={isUploading}
                    className="text-sm text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Adjust position
                  </button>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 bg-gray-800/50 backdrop-blur-md text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600 border border-gray-700"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-300 mb-2">
                Location
              </label>
              <div ref={locationSearchRef} className="relative">
                <div className="relative">
                  <input
                    type="text"
                    id="location"
                    value={locationQuery}
                    onChange={(e) => setLocationQuery(e.target.value)}
                    placeholder="Search for a location..."
                    className="w-full px-4 py-2 pl-10 bg-gray-800/50 backdrop-blur-md text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600 border border-gray-700"
                  />
                  <MapPin className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  {isSearchingLocation && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    </div>
                  )}
                </div>

                {selectedLocation && !locationQuery && (
                  <div className="mt-2 px-3 py-2 bg-gray-800/50 backdrop-blur-md rounded-lg border border-gray-700">
                    <p className="text-sm text-white truncate">{selectedLocation}</p>
                  </div>
                )}

                {locations.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-900/95 backdrop-blur-xl rounded-lg shadow-lg border border-gray-700 max-h-60 overflow-auto">
                    {locations.map((location, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleLocationSelect(location)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-800/50 text-white text-sm"
                      >
                        {location.display_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-300 mb-2">
                Bio
              </label>
              <textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 bg-gray-800/50 backdrop-blur-md text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600 border border-gray-700 resize-none"
                placeholder="Tell us about yourself..."
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProfileUpdating || isUploading}
              className="px-4 py-2 bg-gray-800/50 backdrop-blur-md text-white rounded-lg hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProfileUpdating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;